import React, { useState, useEffect } from "react";
import { PulseButton } from "@pulselabs-ai/shared-fe-ui";
import "./InstallSDKModal.scss";

import Step1Developer from "./steps/Step1Developer";
import Step2SDK from "./steps/Step2SDK";
import Step3Platform from "./steps/Step3Platform";
import Step4ConfigurationAndroid from "./steps/Step4ConfigurationAndroid";
import Step4ConfigurationFlutter from "./steps/Step4ConfigurationFlutter";
import Step4ConfigurationiOS from "./steps/Step4ConfigurationiOS";
import Step4ConfigurationReactNative from "./steps/Step4ConfigurationReactNative";
import Step5Installation from "./steps/Step5Installation";
import Step6Invite from "./steps/Step6Invite";
import MfaAppService from "../../services/MfaAppService";
import { useSelector } from "react-redux";
import type { RootState } from "../../store/store";
import Step3Web from "./steps/Step3Web";

interface InstallSDKModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
}

interface SDKSetupState {
  currentStep: number;
  isDeveloper: string;
  sdkType: string;
  productType: string;
  programmingLanguage: string;
  uiEntryPoint: string;
  usesScenes: string;
  os: string;
  rootWidgetType: string;
  teamMembers: {
    email: string;
    role: string;
    isSelected: boolean;
  }[];
  webFramework: string;
}

const InstallSDKModal: React.FC<InstallSDKModalProps> = ({
  isOpen,
  onClose,
  projectId,
}) => {
  const { activeWorkspace } = useSelector(
    (state: RootState) => state.workspace,
  );
  const { user } = useSelector((state: RootState) => state.user);
  const [state, setState] = useState<SDKSetupState>({
    currentStep: 1,
    isDeveloper: "",
    sdkType: "",
    productType: "",
    programmingLanguage: "",
    os: "",
    uiEntryPoint: "",
    usesScenes: "",
    rootWidgetType: "",
    teamMembers: [],
    webFramework: "",
  });

  useEffect(() => {
    if (!isOpen) {
      setState({
        currentStep: 1,
        isDeveloper: "",
        sdkType: "",
        productType: "",
        programmingLanguage: "",
        os: "",
        uiEntryPoint: "",
        usesScenes: "",
        rootWidgetType: "",
        teamMembers: [],
        webFramework: "",
      });
    }
  }, [isOpen]);

  const updateState = (field: keyof SDKSetupState, value: string) => {
    setState((prev) => ({ ...prev, [field]: value }));
  };

  const updateConfigurationState = (
    field:
      | "programmingLanguage"
      | "uiEntryPoint"
      | "usesScenes"
      | "os"
      | "rootWidgetType",
    value: string,
  ) => {
    updateState(field, value);
  };

  const handleSendDocumentation = async () => {
    try {
      const selectedMembers = state.teamMembers.filter((m) => m.isSelected);
      const mfaAppService = MfaAppService.getInstance();

      await mfaAppService.sendSDKDocumentation(
        {
          projectId,
          workspaceId: activeWorkspace?.id.toString() || "",
          userAccessList: selectedMembers.map(({ email, role }) => ({
            email,
            role,
          })),
        },
        user?.id.toString() || "",
      );

      onClose();
    } catch (error) {
      console.error("Failed to send documentation:", error);
      // Handle error (show error message to user)
    }
  };

  const handleContinue = () => {
    if (state.currentStep === 1 && state.isDeveloper === "no") {
      // Skip directly to step 6 if user is not a developer
      setState((prev) => ({ ...prev, currentStep: 6 }));
    } else if (state.currentStep === 3 && state.sdkType === "web") {
      // Skip step 4 for web SDKs and go directly to installation step
      setState((prev) => ({ ...prev, currentStep: 5 }));
    } else if (state.currentStep < 5) {
      setState((prev) => ({ ...prev, currentStep: prev.currentStep + 1 }));
    } else if (state.currentStep === 5) {
      // Go from step 5 to step 6
      setState((prev) => ({ ...prev, currentStep: 6 }));
    } else {
      // Only send documentation at step 6
      handleSendDocumentation();
    }
  };

  const androidConfig = {
    programmingLanguage: state.programmingLanguage,
  };

  const iosConfig = {
    programmingLanguage: state.programmingLanguage,
    uiEntryPoint: state.uiEntryPoint,
    usesScenes: state.usesScenes,
  };

  const flutterConfig = {
    rootWidgetType: state.rootWidgetType,
  };

  const reactNativeConfig = {
    os: state.os,
  };

  const installationProps = {
    productType: state.sdkType === "web" ? "web" : state.productType,
    programmingLanguage:
      state.sdkType === "web" ? state.webFramework : state.programmingLanguage,
    usesScenes: state.usesScenes,
    uiEntryPoint: state.uiEntryPoint,
    rootWidgetType: state.rootWidgetType,
    os: state.os,
  };

  const renderStep = () => {
    switch (state.currentStep) {
      case 1:
        return (
          <Step1Developer
            isDeveloper={state.isDeveloper}
            onUpdateState={(value) => updateState("isDeveloper", value)}
          />
        );
      case 2:
        return (
          <Step2SDK
            sdkType={state.sdkType}
            onUpdateState={(value) => updateState("sdkType", value)}
          />
        );
      case 3:
        if (state.sdkType === "web") {
          return (
            <Step3Web
              webFramework={state.webFramework}
              onUpdateState={(value) => updateState("webFramework", value)}
            />
          );
        } else {
          return (
            <Step3Platform
              productType={state.productType}
              onUpdateState={(value) => updateState("productType", value)}
            />
          );
        }
      case 4:
        switch (state.productType) {
          case "android":
            return (
              <Step4ConfigurationAndroid
                {...androidConfig}
                onUpdateState={updateConfigurationState}
              />
            );
          case "ios":
            return (
              <Step4ConfigurationiOS
                {...iosConfig}
                onUpdateState={updateConfigurationState}
              />
            );
          case "flutter":
            return (
              <Step4ConfigurationFlutter
                {...flutterConfig}
                onUpdateState={updateConfigurationState}
              />
            );
          case "reactnative":
            return (
              <Step4ConfigurationReactNative
                {...reactNativeConfig}
                onUpdateState={updateConfigurationState}
              />
            );
          default:
            return null;
        }
      case 5:
        return <Step5Installation {...installationProps} />;
      case 6:
        return (
          <Step6Invite
            teamMembers={state.teamMembers}
            onUpdateTeamMembers={(members) =>
              setState((prev) => ({ ...prev, teamMembers: members }))
            }
          />
        );
      default:
        return null;
    }
  };

  const isStepValid = () => {
    switch (state.currentStep) {
      case 1:
        return state.isDeveloper !== "";
      case 2:
        return state.sdkType !== "";
      case 3:
        if (state.sdkType === "web") {
          return state.webFramework !== "";
        } else {
          // For mobile, just check that a platform is selected
          return state.productType !== "";
        }
      case 4:
        // Platform-specific validations for step 4
        if (state.productType === "android") {
          return state.programmingLanguage !== "";
        } else if (state.productType === "ios") {
          if (state.programmingLanguage === "objective-c" && state.usesScenes)
            return true;
          if (state.programmingLanguage && !state.uiEntryPoint) return false;
          if (state.uiEntryPoint && state.uiEntryPoint === "SwiftUI")
            return true;
          if (
            state.uiEntryPoint &&
            state.uiEntryPoint === "UIKit" &&
            !state.usesScenes
          )
            return false;
          return state.programmingLanguage !== "";
        } else if (state.productType === "reactnative") {
          return state.os !== "";
        } else if (state.productType === "flutter") {
          return state.rootWidgetType !== "";
        }
        return true;
      case 5:
        return true;
      case 6:
        return state.teamMembers.some((member) => member.isSelected);
      default:
        return false;
    }
  };

  return (
    <div className="install-sdk-modal">
      {renderStep()}
      <div className="modal-actions">
        <PulseButton onClick={handleContinue} disabled={!isStepValid()}>
          {state.currentStep === 6 ? "SEND SDK DOCUMENTATION" : "Continue"}
        </PulseButton>
      </div>
    </div>
  );
};

export default InstallSDKModal;
